import { useEffect, useRef } from "react";

import { CheckCircle, Close } from "@material-ui/icons";
import { Input, Tooltip } from "antd";

import { IconSpinner } from "components/icons";
import { useProjectDispatch } from "components/project/projects/context";

import { useStructuredNodeContext } from "../../project-layer-tree/node-manager/context/StructuredNodeContext";
import { setInEditMode } from "../../project-layer-tree/node-manager/context/reducer/structuredNodeReducer";
import { ActionButton, FlexItem } from "../nodes/StyledNodePartials";

export type EditableLabelPropsT = {
  isLoading: boolean;
  onCancel: {
    (e: React.SyntheticEvent): void;
    isLoading?: boolean;
  };
  onChange: {
    (e: React.SyntheticEvent): void;
    isLoading?: boolean;
  };
};

export function EditableLabel(props: EditableLabelPropsT) {
  const { isLoading, onCancel, onChange } = props;

  const projectDispatch = useProjectDispatch();

  const [{ inEditMode, editableTitle, originalTitle }, nodeDispatch] =
    useStructuredNodeContext();

  const labelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // If the menu is open and the clicked target is not within the menu,
      // then close the menu, but only if not selecting text
      const textSelection = window.getSelection();
      const isSelectingText: boolean =
        textSelection?.type === "Range" && textSelection?.toString()?.length > 0;

      if (
        inEditMode &&
        labelRef.current &&
        !labelRef.current.contains(e.target) &&
        !isSelectingText
      ) {
        onChange && onChange(e);
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => {
      // Cleanup the event listener
      document.removeEventListener("click", handleClickOutside);
    };
  }, [inEditMode, editableTitle]);

  const onTextChange = (e) => {
    nodeDispatch({
      type: "setEditableTitle",
      payload: e.target.value
    });
  };

  const onKeyDown = (evt) => {
    //ensure we only listen to the correct keydown event
    //by checking against the tag we set
    if (
      evt.key === "Enter" &&
      (document.activeElement === document.body ||
        document.activeElement.getAttribute("data-tag") === "editLabel")
    ) {
      if (inEditMode) {
        onChange && onChange(evt);
      } else {
        setInEditMode(nodeDispatch, true, (isDraggable) =>
          projectDispatch({ payload: { isTreeDraggable: isDraggable } })
        );
      }
    }

    if (evt.key === "Escape") {
      onCancel(evt);
    }
  };

  return inEditMode ? (
    <FlexItem style={{ width: "100%" }} ref={labelRef}>
      {
        <Input
          className="BaseInput Input TextArea"
          value={editableTitle}
          onChange={onTextChange}
          onKeyDown={onKeyDown}
          autoFocus
          data-tag="editLabel"
        />
      }
      <FlexItem>
        {isLoading && <IconSpinner size={14} />}
        {!isLoading && (
          <>
            <Tooltip placement="top" title="Save">
              <ActionButton onClick={onChange}>
                <CheckCircle style={{ color: "#00C7B1" }} fontSize="large" />
              </ActionButton>
            </Tooltip>
            <Tooltip placement="top" title="Cancel">
              <ActionButton onClick={onCancel}>
                <Close />
              </ActionButton>
            </Tooltip>
          </>
        )}
      </FlexItem>
    </FlexItem>
  ) : (
    <label ref={labelRef}>{originalTitle}</label>
  );
}
